import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Globe, NotePencil, Star } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { BeforePhotoControl } from "@/components/BeforePhotoControl";
import { ProjectStoryDialog } from "@/components/ProjectStoryDialog";
import { photoSrc } from "@/components/Thumb";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkAll } from "@/lib/shrinkImage";

// Finished-work photos for one job: upload the "after" shot, pair a "before", publish.
export const JobFinishedPhotos = ({ jobId }) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [story, setStory] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/completion-photos", { params: { job_id: jobId } })
        .then(({ data }) => setItems(data))
        .catch(() => {}),
    [jobId],
  );
  useEffect(() => {
    load();
  }, [load]);

  const replace = (doc) =>
    setItems((list) => list.map((x) => (x.id === doc.id ? doc : x)));

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setBusy(true);
    try {
      for (const f of await shrinkAll(picked)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("job_id", jobId);
        await api.post("/completion-photos", fd);
      }
      toast.success(picked.length === 1 ? "Finished photo saved" : `${picked.length} photos saved`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const toggleWebsite = async (item) => {
    const show = !item.show_on_website;
    try {
      const { data } = await api.patch(`/completion-photos/${item.id}/website`, { show });
      replace(data);
      toast.success(show ? "Added to the website gallery" : "Taken off the website");
    } catch {
      toast.error("Could not update the website gallery");
    }
  };

  return (
    <div data-testid="job-finished-photos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-zinc-500">
          Photos of the finished install. Pair a before shot and tick "Show on site" for a
          swipe-to-compare slider in the website gallery.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={upload}
          data-testid="job-finished-upload-input"
        />
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="job-finished-upload-button"
          className="h-9 bg-indigo-500 text-[11px] uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Camera size={14} weight="bold" className="mr-2" />
          {busy ? "Uploading…" : "Add finished photo"}
        </Button>
      </div>

      {items.length === 0 ? (
        <p
          className="mt-4 rounded-lg border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500"
          data-testid="job-finished-empty"
        >
          No finished photos yet.{" "}
          <Link to="/completion-photos" className="text-indigo-300 hover:underline">
            Completion photos page →
          </Link>
        </p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="job-finished-grid">
          {items.map((i) => (
            <div
              key={i.id}
              data-testid={`job-finished-tile-${i.id}`}
              className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
            >
              <div className="relative">
                <img src={photoSrc(i.photo, true)} alt={i.slab_color} className="h-36 w-full object-cover" />
                {i.featured && (
                  <span className="absolute right-2 top-2 rounded bg-yellow-500/90 p-1 text-black">
                    <Star size={11} weight="fill" />
                  </span>
                )}
              </div>
              <div className="p-2.5">
                <BeforePhotoControl item={i} onChange={replace} compact />
                <button
                  type="button"
                  data-testid={`job-finished-website-${i.id}`}
                  onClick={() => toggleWebsite(i)}
                  className={`mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    i.show_on_website
                      ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  <Globe size={13} weight="bold" />
                  {i.show_on_website ? "On website" : "Show on site"}
                </button>
                <button
                  type="button"
                  data-testid={`job-finished-story-${i.id}`}
                  onClick={() => setStory(i)}
                  title="Write the case study while it's fresh: story, edge profile, sink"
                  className={`mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    i.story || i.edge_profile || i.sink
                      ? "border-sky-500/50 bg-sky-500/15 text-sky-300"
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  <NotePencil size={13} weight="bold" />
                  {i.story ? "Case study" : "Write the story"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {story && (
        <ProjectStoryDialog
          item={story}
          onClose={() => setStory(null)}
          onSaved={(doc) => {
            replace(doc);
            setStory(null);
          }}
        />
      )}
    </div>
  );
};
